import { useSWRInfinite } from "swr";
import { newsFetcher } from "lib/http/api";
import { NewsFeedArticle, LIMIT } from "core/feed/types";
import { useEffect, useRef, useState } from "react";
import { setupQueryParams } from "core/feed/service";
import { FeedValues } from "lib/context/FeedContext";

export const NEWS_FEED_ENDPOINT = "maritime/news";

export interface GetNewsFeedParams {
  feed: NewsFeedArticle[] | null;
  query: string;
  setFeedValues: (value: Partial<FeedValues>) => void;
  xToken?: string | null;
  userRefId: string | null;
  initialData?: NewsFeedArticle[] | null;
  initialHeader: number | null;
}

export interface NewsHeaders {
  num_total_jobs?: number;
  num_results?: number;
}

export interface NewsFeedArticleRes {
  data: NewsFeedArticle[];
  headers: NewsHeaders;
}

export default function useGetNewsFeed(params: GetNewsFeedParams) {
  const {
    setFeedValues,
    feed,
    query,
    xToken,
    userRefId,
    initialData,
    initialHeader,
  } = params;
  const [hasMore, setHasMore] = useState<boolean>(false);
  const newsCounts = useRef<number>(initialHeader ?? 0);

  const getKey = (index: number, previousPageData: any | null) => {
    if (previousPageData?.data && !previousPageData?.data.length) return null;
    const queryParams = setupQueryParams({
      index,
      limit: LIMIT,
      query,
    });
    const unAuthEndpoint = `v1/web/maritime/news${queryParams}`;
    const authEndpoint = `v1/maritime/news${queryParams}`;
    const endpoint = userRefId ? authEndpoint : xToken ? unAuthEndpoint : null;
    return endpoint;
  };

  // Reset hasMore on query change
  useEffect(() => {
    setHasMore(true);
  }, [query]);
  const initData: NewsFeedArticleRes = {
    data: initialData ?? [],
    headers: {
      num_results: initialHeader ?? 0,
      num_total_jobs: initialHeader ?? 0,
    },
  };
  const {
    data: pageArrays,
    error,
    isValidating,
    size,
    setSize,
  } = useSWRInfinite<NewsFeedArticleRes>(getKey, newsFetcher, {
    initialData: initialData ? [initData] : undefined,
    initialSize: 1,
    persistSize: true,
    revalidateAll: false,
  });

  useEffect(() => {
    const tempFeed: NewsFeedArticle[] = [];
    if (pageArrays && pageArrays[0].data) {
      pageArrays.forEach((currPage) => {
        if (currPage && currPage.headers) {
          newsCounts.current = currPage.headers?.num_results ?? 0;
        }
        if (currPage.data.length < LIMIT) {
          setHasMore(false);
        }
        tempFeed.push(...currPage.data);
      });
    }
    setFeedValues({ feed: tempFeed });
  }, [pageArrays, setFeedValues, setHasMore]);

  const isLoading =
    (!pageArrays && !error) ||
    // @ts-ignore
    (!error && pageArrays && pageArrays[0]?.length && !feed?.length) ||
    isValidating;

  return {
    error,
    feed: error ? [] : feed,
    hasMore,
    isLoading,
    setSize,
    size,
    total: newsCounts.current,
  };
}
